/**
 * Enterprise Well Departure Reminder Service
 * 
 * Monitors user location relative to wells and triggers reminders
 * when users leave a well without logging production data.
 * 
 * Features:
 * - Geofence-based entry/exit detection with hysteresis
 * - 5-minute minimum dwell time requirement
 * - Cooldown to prevent notification spam
 * - Integrates with user notification preferences
 * - Offline-capable with local storage fallback
 * 
 * @module wellDepartureService
 */

import { getCompanyStorageKey } from '../tenantContext';

// ==================== CONFIGURATION ====================

/** Distance thresholds in meters */
const GEOFENCE_CONFIG = {
  /** Radius for entering well zone (smaller = more precise) */
  ENTRY_RADIUS_METERS: 200,
  /** Radius for exiting well zone (larger = hysteresis prevents rapid toggling) */
  EXIT_RADIUS_METERS: 300,
  /** Minimum time spent at well to trigger reminder on departure */
  MIN_DWELL_TIME_MS: 5 * 60 * 1000, // 5 minutes
  /** Cooldown before same well triggers another reminder */
  REMINDER_COOLDOWN_MS: 4 * 60 * 60 * 1000, // 4 hours
  /** How often to check position against wells */
  CHECK_INTERVAL_MS: 10 * 1000, // 10 seconds
  /** Maximum age of location data to consider valid */
  LOCATION_MAX_AGE_MS: 30 * 1000, // 30 seconds
};

// ==================== TYPES ====================

export interface WellLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

export interface WellVisitState {
  wellId: string;
  wellName: string;
  enteredAt: number;
  lastSeenAt: number;
  /** True if user has been at well for minimum dwell time */
  dwellMet: boolean;
  /** Has a production entry been created since arrival? */
  entryCreated: boolean;
}

export interface WellDepartureEvent {
  wellId: string;
  wellName: string;
  dwellDurationMs: number;
  departureTriggerTime: number;
}

export interface DepartureReminderCallback {
  (event: WellDepartureEvent): void;
}

export interface NotificationPreferences {
  enabled: boolean;
  missedDataEntry?: {
    enabled: boolean;
    sound: boolean;
    vibrate: boolean;
  };
}

interface CooldownRecord {
  wellId: string;
  lastReminderAt: number;
}

// ==================== SERVICE CLASS ====================

class WellDepartureService {
  private isActive: boolean = false;
  private watchId: number | null = null;
  private checkIntervalId: ReturnType<typeof setInterval> | null = null;
  
  private wells: WellLocation[] = [];
  private currentVisits: Map<string, WellVisitState> = new Map();
  private cooldowns: Map<string, number> = new Map();
  private recentEntryWellIds: Set<string> = new Set();
  
  private onDepartureCallback: DepartureReminderCallback | null = null;
  private notificationPrefs: NotificationPreferences = { enabled: true };
  
  private lastPosition: GeolocationPosition | null = null;
  private userId: string | null = null;

  // ==================== PUBLIC API ====================

  /**
   * Start monitoring well departures
   */
  start(config: {
    wells: WellLocation[];
    userId: string;
    onDeparture: DepartureReminderCallback;
    notificationPrefs?: NotificationPreferences;
  }): void {
    if (this.isActive) {
      console.log('[WellDeparture] Already active, updating config');
      this.wells = config.wells.filter(w => w.latitude && w.longitude);
      this.notificationPrefs = config.notificationPrefs || { enabled: true };
      return;
    }

    console.log('[WellDeparture] Starting service with', config.wells.length, 'wells');
    
    this.wells = config.wells.filter(w => w.latitude && w.longitude);
    this.userId = config.userId;
    this.onDepartureCallback = config.onDeparture;
    this.notificationPrefs = config.notificationPrefs || { enabled: true };
    
    // Load persisted state
    this.loadPersistedState();
    
    // Start location watching
    this.startLocationWatch();
    
    // Start periodic check
    this.checkIntervalId = setInterval(() => {
      this.checkDepartures();
    }, GEOFENCE_CONFIG.CHECK_INTERVAL_MS);
    
    this.isActive = true;
    console.log('[WellDeparture] Service started');
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (!this.isActive) return; // Already stopped, no-op
    
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    
    if (this.checkIntervalId !== null) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    
    // Persist state before stopping
    this.persistState();
    
    this.isActive = false;
    // Debug only - removed noisy logs
  }

  /**
   * Update wells list (e.g., after data refresh)
   */
  updateWells(wells: WellLocation[]): void {
    this.wells = wells.filter(w => w.latitude && w.longitude);
    console.log('[WellDeparture] Updated wells:', this.wells.length);
  }

  /**
   * Update notification preferences
   */
  updateNotificationPrefs(prefs: NotificationPreferences): void {
    this.notificationPrefs = prefs;
    console.log('[WellDeparture] Updated prefs:', prefs);
  }

  /**
   * Mark that a production entry was created for a well.
   * Called when user saves data entry.
   */
  markEntryCreated(wellId: string): void {
    const visit = this.currentVisits.get(wellId);
    if (visit) {
      visit.entryCreated = true;
      console.log('[WellDeparture] Marked entry created for', wellId);
    }
    
    // Also track in recent entries (backup)
    this.recentEntryWellIds.add(wellId);
    
    // Clear from recent after 1 hour
    setTimeout(() => {
      this.recentEntryWellIds.delete(wellId);
    }, 60 * 60 * 1000);
  }

  /**
   * Dismiss reminder for a well (add cooldown without logging entry)
   */
  dismissReminder(wellId: string): void {
    this.cooldowns.set(wellId, Date.now());
    this.persistState();
    console.log('[WellDeparture] Dismissed reminder for', wellId);
  }

  /**
   * Get current status for debugging
   */
  getStatus(): {
    isActive: boolean;
    wellCount: number;
    activeVisits: WellVisitState[];
    cooldownCount: number;
  } {
    return {
      isActive: this.isActive,
      wellCount: this.wells.length,
      activeVisits: Array.from(this.currentVisits.values()),
      cooldownCount: this.cooldowns.size,
    };
  }

  // ==================== PRIVATE METHODS ====================

  private startLocationWatch(): void {
    if (!navigator.geolocation) {
      console.warn('[WellDeparture] Geolocation not supported');
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.lastPosition = position;
        this.processLocationUpdate(position);
      },
      (error) => {
        console.warn('[WellDeparture] Location error:', error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: GEOFENCE_CONFIG.LOCATION_MAX_AGE_MS,
      }
    );
  }

  private processLocationUpdate(position: GeolocationPosition): void {
    const { latitude, longitude, accuracy } = position.coords;
    const now = Date.now();

    // Skip if accuracy is too poor
    if (accuracy > 100) {
      console.log('[WellDeparture] Skipping update, accuracy too low:', accuracy);
      return;
    }

    // Check each well for entry/exit
    for (const well of this.wells) {
      const distance = this.calculateDistance(
        latitude, longitude,
        well.latitude, well.longitude
      );

      const visit = this.currentVisits.get(well.id);

      if (visit) {
        // Currently visiting this well
        if (distance > GEOFENCE_CONFIG.EXIT_RADIUS_METERS) {
          // User has exited
          this.handleWellExit(well, visit, now);
        } else {
          // Still at well, update last seen
          visit.lastSeenAt = now;
          
          // Check if dwell time met
          if (!visit.dwellMet && (now - visit.enteredAt >= GEOFENCE_CONFIG.MIN_DWELL_TIME_MS)) {
            visit.dwellMet = true;
            console.log('[WellDeparture] Dwell time met for', well.name);
          }
        }
      } else {
        // Not currently visiting this well
        if (distance <= GEOFENCE_CONFIG.ENTRY_RADIUS_METERS) {
          // User has entered
          this.handleWellEntry(well, now);
        }
      }
    }

    this.persistState();
  }

  private handleWellEntry(well: WellLocation, timestamp: number): void {
    console.log('[WellDeparture] Entered well zone:', well.name);
    
    const visit: WellVisitState = {
      wellId: well.id,
      wellName: well.name,
      enteredAt: timestamp,
      lastSeenAt: timestamp,
      dwellMet: false,
      entryCreated: this.recentEntryWellIds.has(well.id),
    };
    
    this.currentVisits.set(well.id, visit);
  }

  private handleWellExit(well: WellLocation, visit: WellVisitState, timestamp: number): void {
    console.log('[WellDeparture] Exited well zone:', well.name, {
      dwellMet: visit.dwellMet,
      entryCreated: visit.entryCreated,
    });
    
    // Remove from active visits
    this.currentVisits.delete(well.id);

    // Check if reminder should trigger
    if (this.shouldTriggerReminder(well.id, visit)) {
      const event: WellDepartureEvent = {
        wellId: well.id,
        wellName: well.name,
        dwellDurationMs: timestamp - visit.enteredAt,
        departureTriggerTime: timestamp,
      };
      
      this.triggerReminder(event);
    }
  }

  private shouldTriggerReminder(wellId: string, visit: WellVisitState): boolean {
    // Check if notifications are enabled
    if (!this.notificationPrefs.enabled) {
      console.log('[WellDeparture] Notifications disabled globally');
      return false;
    }

    // Check specific notification type preference
    const missedEntryPrefs = this.notificationPrefs.missedDataEntry;
    if (missedEntryPrefs && !missedEntryPrefs.enabled) {
      console.log('[WellDeparture] Missed data entry notifications disabled');
      return false;
    }

    // Check if dwell time was met
    if (!visit.dwellMet) {
      console.log('[WellDeparture] Dwell time not met, skipping reminder');
      return false;
    }

    // Check if entry was already created
    if (visit.entryCreated) {
      console.log('[WellDeparture] Entry already created, skipping reminder');
      return false;
    }

    // Check if recently logged entry (backup check)
    if (this.recentEntryWellIds.has(wellId)) {
      console.log('[WellDeparture] Recent entry found, skipping reminder');
      return false;
    }

    // Check cooldown
    const lastReminder = this.cooldowns.get(wellId);
    if (lastReminder && (Date.now() - lastReminder < GEOFENCE_CONFIG.REMINDER_COOLDOWN_MS)) {
      console.log('[WellDeparture] Cooldown active, skipping reminder');
      return false;
    }

    return true;
  }

  private triggerReminder(event: WellDepartureEvent): void {
    console.log('[WellDeparture] Triggering reminder for', event.wellName);
    
    // Set cooldown
    this.cooldowns.set(event.wellId, Date.now());
    this.persistState();
    
    // Trigger callback
    if (this.onDepartureCallback) {
      this.onDepartureCallback(event);
    }

    // Vibrate if enabled and supported
    const missedEntryPrefs = this.notificationPrefs.missedDataEntry;
    if (missedEntryPrefs?.vibrate && 'vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  }

  private checkDepartures(): void {
    // This is a secondary check in case location updates stop
    const now = Date.now();
    
    for (const [wellId, visit] of this.currentVisits.entries()) {
      // If we haven't seen the user at this well recently, consider them departed
      if (now - visit.lastSeenAt > 60000) { // 1 minute without update
        const well = this.wells.find(w => w.id === wellId);
        if (well) {
          console.log('[WellDeparture] Stale visit detected for', visit.wellName);
          this.handleWellExit(well, visit, now);
        }
      }
    }
  }

  /**
   * Calculate distance between two coordinates in meters using Haversine formula
   */
  private calculateDistance(
    lat1: number, lon1: number,
    lat2: number, lon2: number
  ): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // ==================== PERSISTENCE ====================

  private getStorageKey(): string {
    const baseKey = `wellDepartureState_${this.userId || 'anonymous'}`;
    return getCompanyStorageKey(baseKey);
  }

  private persistState(): void {
    try {
      const state = {
        visits: Array.from(this.currentVisits.entries()),
        cooldowns: Array.from(this.cooldowns.entries()),
        recentEntries: Array.from(this.recentEntryWellIds),
        persistedAt: Date.now(),
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch (error) {
      console.warn('[WellDeparture] Failed to persist state:', error);
    }
  }

  private loadPersistedState(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (!stored) return;
      
      const state = JSON.parse(stored);
      const now = Date.now();
      
      // Only restore if persisted less than 1 hour ago
      if (now - state.persistedAt > 60 * 60 * 1000) {
        console.log('[WellDeparture] Persisted state too old, discarding');
        return;
      }
      
      // Restore visits (filter out stale ones)
      if (state.visits) {
        for (const [wellId, visit] of state.visits) {
          if (now - visit.lastSeenAt < 30 * 60 * 1000) { // 30 minutes
            this.currentVisits.set(wellId, visit);
          }
        }
      }
      
      // Restore cooldowns
      if (state.cooldowns) {
        for (const [wellId, timestamp] of state.cooldowns) {
          if (now - timestamp < GEOFENCE_CONFIG.REMINDER_COOLDOWN_MS) {
            this.cooldowns.set(wellId, timestamp);
          }
        }
      }
      
      // Restore recent entries
      if (state.recentEntries) {
        for (const wellId of state.recentEntries) {
          this.recentEntryWellIds.add(wellId);
        }
      }
      
      console.log('[WellDeparture] Restored state:', {
        visits: this.currentVisits.size,
        cooldowns: this.cooldowns.size,
        recentEntries: this.recentEntryWellIds.size,
      });
    } catch (error) {
      console.warn('[WellDeparture] Failed to load persisted state:', error);
    }
  }
}

// ==================== SINGLETON EXPORT ====================

export const wellDepartureService = new WellDepartureService();

// Export config for external use
export const WELL_DEPARTURE_CONFIG = GEOFENCE_CONFIG;
